import { faDeleteLeft, faPhone, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Call } from '@twilio/voice-sdk';
import { useCallback, useEffect, useState } from 'react';

const KEYS: { digit: string; sub?: string }[] = [
  { digit: '1' },
  { digit: '2', sub: 'ABC' },
  { digit: '3', sub: 'DEF' },
  { digit: '4', sub: 'GHI' },
  { digit: '5', sub: 'JKL' },
  { digit: '6', sub: 'MNO' },
  { digit: '7', sub: 'PQRS' },
  { digit: '8', sub: 'TUV' },
  { digit: '9', sub: 'WXYZ' },
  { digit: '*' },
  { digit: '0', sub: '+' },
  { digit: '#' }
];

type NumberPadProps =
  | {
      mode: 'dtmf';
      activeCall: Call;
      onClose: () => void;
      allowDelete?: boolean;
      title?: string;
      className?: string;
    }
  | {
      mode: 'dialpad';
      onDial: (number: string) => void;
      onClose: () => void;
      callDisabled?: boolean;
      title?: string;
      className?: string;
    };

const VALID_KEYS = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#']);

export const NumberPad = (props: NumberPadProps) => {
  const { onClose, title, className = '' } = props;
  const isDtmf = props.mode === 'dtmf';
  const activeCall = isDtmf ? props.activeCall : null;
  const onDial = !isDtmf ? props.onDial : null;
  const callDisabled = !isDtmf ? (props.callDisabled ?? false) : false;
  const [input, setInput] = useState('');

  const handleKey = useCallback(
    (digit: string) => {
      activeCall?.sendDigits(digit);
      setInput((prev) => prev + digit);
    },
    [activeCall]
  );

  const handleDelete = useCallback(() => {
    setInput((prev) => prev.slice(0, -1));
  }, []);

  const handleDial = useCallback(() => {
    if (onDial && input.trim() && !callDisabled) {
      onDial(input.trim());
    }
  }, [onDial, input, callDisabled]);

  const showDelete = !isDtmf || props.allowDelete;

  useEffect(() => {
    const onPaste = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const text = (e as any).clipboardData?.getData('text') ?? '';
      const valid = text.replace(/[^\d*#]/g, '');
      if (!valid) return;
      e.preventDefault();
      valid.split('').forEach((d: string) => handleKey(d));
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleKey]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      if (VALID_KEYS.has(e.key)) {
        e.preventDefault();
        handleKey(e.key);
      } else if (e.key === 'Backspace' && showDelete) {
        e.preventDefault();
        handleDelete();
      } else if (!isDtmf && !callDisabled && e.key === 'Enter') {
        e.preventDefault();
        handleDial();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleKey, handleDelete, handleDial, isDtmf, callDisabled, showDelete]);

  return (
    <div
      className={`bg-gray-900 text-white rounded-xl px-4 pt-2 pb-3 shadow-lg min-w-56 w-full flex flex-col gap-2 ${className}`}>
      <div className="flex items-center justify-between">
        {title ? (
          <span className="text-xs font-medium text-gray-400 tracking-wide">{title}</span>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-white transition-colors"
          aria-label="Close keypad">
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
        </button>
      </div>

      <div className="h-7 flex items-center bg-gray-800 rounded px-2 gap-1">
        <span className="flex-1 text-sm font-mono tracking-widest text-gray-100 truncate text-center">
          {input || <span className="text-gray-600 text-xs">—</span>}
        </span>
        {showDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={input.length === 0}
            aria-label="Delete last digit"
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0">
            <FontAwesomeIcon icon={faDeleteLeft} className="text-sm" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {KEYS.map(({ digit, sub }) => (
          <button
            key={digit}
            type="button"
            onClick={() => handleKey(digit)}
            className="flex flex-col items-center justify-center h-12 rounded-lg bg-gray-700 hover:bg-gray-600 active:bg-gray-500 transition-colors select-none"
            aria-label={`Dial ${digit}`}>
            <span className="text-base font-semibold leading-none">{digit}</span>
            {sub && <span className="text-[9px] text-gray-400 mt-0.5 leading-none">{sub}</span>}
          </button>
        ))}
      </div>

      {!isDtmf && (
        <button
          type="button"
          onClick={handleDial}
          disabled={input.trim().length === 0 || callDisabled}
          aria-label="Start call"
          className="mt-1 w-full h-10 rounded-lg bg-green-600 hover:bg-green-500 active:bg-green-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium text-sm">
          <FontAwesomeIcon icon={faPhone} />
          Call
        </button>
      )}
    </div>
  );
};
